import { FC, useMemo, useRef, useState } from 'react';
import { Modal as QcsModal } from '@mui/material';

import { QcsModalBox } from '../../../common/basic/QcsModalBox';
import { SectionHeader } from '../../../common/SectionHeader';
import { GridList } from '../../../common/grid/GridList';
import { QcsTableCell } from '../../../common/basic/QcsTableCell';
import { QcsLoadingButton } from '../../../common/basic/QcsLoadingButton';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useAppSelector } from '../../../../store';
import { getMediaToken } from '../../../../utils/storage';
import { BASE_URL_WITH_VERSION, mediaApi } from '../../../../utils/api';
import { DocumentInputUploadModal } from '../../../risk/DocumentInputUploadModal';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { InnerTooltipWrapper, QcsTooltip } from '../../../common/basic/QcsTooltip';
import { selectIdentity } from '../../../../store/entities/identity';
import { hasRole } from '../../../../utils/roles';
import { setErrorSnacks } from '../../../../utils/error';
import { getLangNameObj } from '../../../../utils/format';
import { useInvitation } from './invitationFunctions';
import { ConfirmDialog } from '../../../common/ConfirmDialog';
import { ShowImageButton } from './ShowImageButton';
import { InvitationActivityDto } from '@api/models/invitation/InvitationActivityDto';
import { UserRole } from '@api/models/enums/UserRole';
import { InvitationState } from '@api/models/enums/InvitationState';
import { FileUploadMediaResponse } from '@api/models/invitation/FileUploadMediaResponse';
import { invitationService } from '@api/services/services';

interface Props {
  itemId?: string;
  onClose: () => void;
}

export const ActivityRiskModal: FC<Props> = ({ itemId, onClose }) => {
  const { t, i18n, invitation, callWithLock, reloadInvitation, enqueueErrorSnackbar, isSaving, setIsSaving } = useInvitation();

  const [uploadModal, setUploadModal] = useState<InvitationActivityDto>();
  const mediaToken = getMediaToken();
  const identity = useAppSelector(selectIdentity);

  const fileRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const item = invitation.activities?.find((x) => x.id === itemId);
  const list = useMemo(() => (item ? [item] : []), [item]);

  const handleRenderData = (item: InvitationActivityDto) => {
    const alreadyApproved = !!item.approvedOn;
    const uploadDisabled =
      invitation.state === InvitationState.Archived ||
      alreadyApproved ||
      !hasRole(identity.roles, [UserRole.ManagerWorkplace, UserRole.ManagerOsah, UserRole.ExternalManager]);
    const downloadDisabled = !item.document;
    const approveDisabled = !hasRole(identity.roles, [UserRole.ManagerOsah, UserRole.ManagerWorkplace]) || !item.document;
    const deleteDisabled = !item.document || alreadyApproved || invitation.state === InvitationState.Archived;

    return (
      <>
        <QcsTableCell>{getLangNameObj(i18n, item.activity)}</QcsTableCell>
        <QcsTableCell align="right">
          <QcsTooltip title={`${t('invitation.tabs.tooltips.uploadRiskDocument')} ${t('common.fileMaxSize')}`} placement="top">
            <InnerTooltipWrapper>
              <QcsLoadingButton onClick={handleUpload(item)} loading={isSaving} disabled={uploadDisabled}>
                <CloudUploadIcon color={uploadDisabled ? undefined : item.document ? 'success' : 'warning'} />
              </QcsLoadingButton>
            </InnerTooltipWrapper>
          </QcsTooltip>

          <ShowImageButton
            tooltipTitle={t('invitation.tabs.tooltips.downloadRiskDocument')}
            documentUrl={`${BASE_URL_WITH_VERSION}${item.document?.path}?token=${mediaToken}`}
            disabled={downloadDisabled}
            color="success"
            fileName={item.document?.fileName}
          />

          <QcsTooltip title={t('invitation.tabs.tooltips.approveRisk')} placement="top">
            <InnerTooltipWrapper>
              <QcsLoadingButton onClick={handleApprove(item)} disabled={approveDisabled || alreadyApproved} loading={isSaving}>
                <CheckCircleIcon color={approveDisabled ? undefined : alreadyApproved ? 'success' : 'warning'} />
              </QcsLoadingButton>
            </InnerTooltipWrapper>
          </QcsTooltip>

          <QcsTooltip title={t('invitation.tabs.tooltips.deleteRisk')} placement="top">
            <InnerTooltipWrapper>
              <QcsLoadingButton onClick={() => setShowConfirmModal(true)} disabled={deleteDisabled} loading={isSaving}>
                <DeleteForeverIcon color={deleteDisabled ? undefined : 'error'} />
              </QcsLoadingButton>
            </InnerTooltipWrapper>
          </QcsTooltip>
        </QcsTableCell>
        <ConfirmDialog
          onClose={() => setShowConfirmModal(false)}
          onConfirm={showConfirmModal ? handleDelete(item) : null}
          title={t('document.riskDocumentDeleteDialog.title')}
          message={t('document.riskDocumentDeleteDialog.message')}
        />
      </>
    );
  };

  const handleUpload = (item: InvitationActivityDto) => () => {
    setUploadModal(item);
    fileRef.current?.click();
  };

  const handleFileChange = (files: FileList | null) => {
    if (!fileRef.current || !files) {
      return;
    }

    const file = files[0];
    setSelectedFile(file);
    fileRef.current.value = '';
  };

  const handleFileUploaded = async (document: FileUploadMediaResponse) => {
    if (!selectedFile || !uploadModal) {
      return;
    }

    await callWithLock(async () => {
      await invitationService.addDocumentsForActivities(invitation.id!, {
        data: [{ activityId: uploadModal.activity!.id!, documentId: document.id! }],
      });
      await reloadInvitation();
    });

    setSelectedFile(null);
  };

  const handleFileCancel = () => {
    setSelectedFile(null);
  };

  const handleApprove = (item: InvitationActivityDto) => async () => {
    setIsSaving(true); // todo: remove once mediaApi.unpairDocumentFromEntity uses new api handler
    await callWithLock(async () => {
      await invitationService.approveActivities(invitation.id!, {
        entries: [item.id!],
      });
      await reloadInvitation();
    });
    setIsSaving(false);
  };

  const handleDelete = (item: InvitationActivityDto) => async () => {
    setIsSaving(true);
    await callWithLock(async () => {
      try {
        await mediaApi.unpairDocumentFromEntity(item.id!, 'FROM_INVITATION_ACTIVITY');
        await reloadInvitation();
      } catch (err) {
        setErrorSnacks(err, enqueueErrorSnackbar);
      }
    });
    setIsSaving(false);
  };

  return (
    <>
      <DocumentInputUploadModal file={selectedFile} onFileUploaded={handleFileUploaded} onCloseOnError={handleFileCancel} />
      <input type="file" ref={fileRef} onChange={({ target }) => handleFileChange(target.files)} hidden />

      <QcsModal open={!!item} onClose={onClose}>
        <QcsModalBox>
          <SectionHeader title="invitation.activity.riskTitle" />
          <GridList<InvitationActivityDto>
            headers={[{ captionStr: 'invitation.activity.name' }, {}]}
            data={list}
            renderData={handleRenderData}
            search={false}
            hidePagination={true}
          />
        </QcsModalBox>
      </QcsModal>
    </>
  );
};
